import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { Grid } from "@mui/material";
import { AutocompleteComponent } from "components/Autocomplete/AutocompleteComponent";
import { ModalV2 } from "components/Modal/ModalV2";
import { buildAccountOptions, buildAccountTypeOptions } from "./accountingAccountRelationHelpers";
import { accountTypeOptions, DEFAULT_OPTIONS, } from "./accountingAccountRelationOptions";
import { getFieldErrorProps } from "helpers/formFieldProps";
import { orderArrayV2 } from "helpers/orderArray";
import { useForm } from "hooks/useForm";
import {
  createAccountingAccountRelation,
  updateAccountingAccountRelation,
} from "redux/actions/accountingAccountRelation/accountingAccountRelationActions";
import {
  ACCOUNTING_ACCOUNT_RELATION_LOADINGS
} from "redux/reducers/accountingAccountRelation/accountingAccountRelationTypes";

const accountingAccountValidations = {
  selectedCompany: { required: true },
  selectedStation: { required: true },
  selectedAccount: { required: true },
  selectedAccountType: { required: true },
};

const { CREATE, UPDATE } = ACCOUNTING_ACCOUNT_RELATION_LOADINGS;

const AccountingAccountRelationForm = ({
  data = {},
  open = false,
  setOpen = () => {},
  titlePrefix = "Nueva",
  update = false,
}) => {
  const dispatch = useDispatch();
  const {
    companiesList,
    stationsList,
  } = useSelector(({ companies }) => companies);
  const {
    accountingAccountList,
    accountingAccountRelationList,
    station,
  } = useSelector(({ accountingAccountRelation }) => accountingAccountRelation);
  const isLoadingComp = useSelector(({ search }) => search.isLoadingComp);

  const accountOptions = buildAccountOptions(accountingAccountList, update ? data : {});
  // buildAccountTypeOptions regresa un arreglo nuevo, así que ordenarlo en
  // sitio no afecta las opciones base.
  const typeOptions = orderArrayV2(
    buildAccountTypeOptions(accountingAccountRelationList, {
      idCompany: station?.id_company,
      excludeId: update ? data.id : undefined,
    }),
    "label",
  );
  const targetAccountType = data.type || DEFAULT_OPTIONS.ACCOUNT_TYPE;
  const loading = isLoadingComp?.[CREATE] || isLoadingComp?.[UPDATE] || false;

  const initialValues = {
    selectedCompany: companiesList.find((company) => company.id === station?.id_company) || null,
    selectedStation: station || null,
    selectedAccount: accountOptions.find((acc) => acc.id === data.idAccountingAccount) || null,
    selectedAccountType: accountTypeOptions.find((acc) => acc.value === targetAccountType) || null,
  };

  const { values, setValues, errors, validate } = useForm(initialValues, accountingAccountValidations);

  const {
    selectedCompany,
    selectedStation,
    selectedAccount,
    selectedAccountType,
  } = values;

  const handleSubmit = () => {
    validate([], () => {
      const form = {
        idCompany: selectedCompany.id,
        idStation: selectedStation.id,
        idAccountingAccount: selectedAccount.id,
        type: selectedAccountType.value,
      };
      if (update) {
        dispatch(updateAccountingAccountRelation(data.id, form, setOpen));
      } else {
        dispatch(createAccountingAccountRelation(form, setOpen));
      }
    });
  };

  return (
    <ModalV2
      title={`${titlePrefix} asignación de cuenta`}
      open={open}
      onClose={setOpen}
      onSubmit={handleSubmit}
      submitBtnText={update ? "Actualizar" :"Asignar"}
      toRight
      isLoading={loading}
    >
      <Grid item xs={12}>
        <AutocompleteComponent
          label="Compañía"
          name="selectedCompany"
          value={selectedCompany}
          handleValues={setValues}
          fullWidth
          variant="outlined"
          textFieldProps={getFieldErrorProps(errors, "selectedCompany")}
          options={companiesList}
          disabled
        />
      </Grid>
      <Grid item xs={12}>
        <AutocompleteComponent
          label="Planta"
          name="selectedStation"
          value={selectedStation}
          handleValues={setValues}
          fullWidth
          variant="outlined"
          textFieldProps={getFieldErrorProps(errors, "selectedStation")}
          options={stationsList}
          disabled
        />
      </Grid>
      <Grid item xs={12}>
        <AutocompleteComponent
          label="Cuenta Contable"
          name="selectedAccount"
          value={selectedAccount}
          handleValues={setValues}
          fullWidth
          variant="outlined"
          textFieldProps={getFieldErrorProps(errors, "selectedAccount")}
          options={accountOptions}
        />
      </Grid>
      <Grid item xs={12}>
        <AutocompleteComponent
          label="Tipo"
          name="selectedAccountType"
          value={selectedAccountType}
          handleValues={setValues}
          fullWidth
          variant="outlined"
          textFieldProps={getFieldErrorProps(errors, "selectedAccountType")}
          options={typeOptions}
          getOptionDisabled={(option) => !!option.disabled}
        />
      </Grid>
    </ModalV2>
  );
};

export default AccountingAccountRelationForm;
